import React from 'react';
import { WaitlistEntry, UnsubscribeFeedback } from './adminTypes';

interface WaitlistPanelProps {
  intradayCount: number;
  loading: boolean;
  entries: WaitlistEntry[];
  selectedEntries: Set<string>;
  resendingEmail: string | null;
  onSelectAll: () => void;
  onToggleEntry: (id: string) => void;
  onClearSelection: () => void;
  onRequestBulkDelete: () => void;
  onResendEmail: (entry: WaitlistEntry) => void;
  onRequestDelete: (id: string) => void;
  onRefresh: () => void;
  onExportCSV: () => void;
  feedbackLoading: boolean;
  onRefreshFeedback: () => void;
  reasonCounts: { [key: string]: number };
  feedback: UnsubscribeFeedback[];
  formatDate: (dateString: string) => string;
}

const WaitlistPanel: React.FC<WaitlistPanelProps> = ({
  intradayCount,
  loading,
  entries,
  selectedEntries,
  resendingEmail,
  onSelectAll,
  onToggleEntry,
  onClearSelection,
  onRequestBulkDelete,
  onResendEmail,
  onRequestDelete,
  onRefresh,
  onExportCSV,
  feedbackLoading,
  onRefreshFeedback,
  reasonCounts,
  feedback,
  formatDate,
}) => (
  <>
    {/* Unsubscribe Feedback */}
    <div className="rp-bento-card mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>Unsubscribe Feedback</h2>
        <div className="flex gap-3">
          <button
            onClick={() => window.open('/.netlify/functions/unsubscribe?token=' + btoa('test@example.com'), '_blank')}
            className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all"
            style={{ color: 'var(--rp-text)' }}
          >
            View Form
          </button>
          <button
            onClick={onRefreshFeedback}
            disabled={feedbackLoading}
            className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
            style={{ color: 'var(--rp-text)' }}
          >
            {feedbackLoading ? 'Loading...' : 'Refresh Feedback'}
          </button>
        </div>
      </div>
      {/* Reason Counts */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        {['not_interested', 'too_many_emails', 'found_alternative', 'app_not_for_me', 'taking_too_long', 'other'].map((reason) => (
          <div key={reason} className="border border-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--rp-text)' }}>{reasonCounts[reason] || 0}</div>
            <div className="text-sm" style={{ color: 'var(--rp-text-secondary)' }}>
              {reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
          </div>
        ))}
      </div>
      {feedback.length === 0 ? (
        <div className="text-center py-8" style={{ color: 'var(--rp-text-tertiary)' }}>No unsubscribe feedback yet</div>
      ) : (
        <div className="space-y-4">
          {feedback.map((item) => (
            <div key={item.id} className="border border-white/10 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-medium" style={{ color: 'var(--rp-text)' }}>{item.email}</span>
                  {item.reason && (
                    <span className="px-2 py-1 text-xs rounded-full bg-white/10" style={{ color: 'var(--rp-text-tertiary)' }}>
                      {item.reason.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <span className="text-sm" style={{ color: 'var(--rp-text-tertiary)' }}>{formatDate(item.created_at)}</span>
              </div>
              {item.feedback && (
                <p style={{ color: 'var(--rp-text-secondary)' }} className="text-sm">{item.feedback}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Waitlist Actions Bar */}
    <div className="rp-bento-card mb-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>Waitlist Entries</h2>
          <div className="flex gap-4">
            <p style={{ color: 'var(--rp-text-secondary)' }}>{entries.length} total entries</p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>•</p>
            <p style={{ color: 'var(--rp-text-secondary)' }}>{intradayCount} added today</p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {selectedEntries.size > 0 && (
            <>
              <button
                onClick={onRequestBulkDelete}
                className="admin-btn admin-btn-danger flex items-center gap-2"
              >
                Delete {selectedEntries.size} Selected
              </button>
              <button
                onClick={onClearSelection}
                className="px-3 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all text-sm"
                style={{ color: 'var(--rp-text)' }}
              >
                Clear Selection
              </button>
            </>
          )}
          <button
            onClick={onExportCSV}
            disabled={entries.length === 0}
            className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
            style={{ color: 'var(--rp-text)' }}
          >
            Export CSV
          </button>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
            style={{ color: 'var(--rp-text)' }}
          >
            {loading ? 'Refreshing...' : 'Refresh Waitlist'}
          </button>
        </div>
      </div>
    </div>

    {/* Data Table */}
    <div className="rp-bento-card">
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
          <p style={{ color: 'var(--rp-text-secondary)' }}>Loading entries...</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-lg" style={{ color: 'var(--rp-text-secondary)' }}>No waitlist entries found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 w-12" style={{ color: 'var(--rp-text)' }}>
                  <input
                    type="checkbox"
                    checked={selectedEntries.size === entries.length && entries.length > 0}
                    onChange={onSelectAll}
                    aria-label="Select all entries"
                    className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-2"
                  />
                </th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Name</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Email</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Location</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Motorcycle</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Source</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Date</th>
                <th className="text-right py-3 px-4" style={{ color: 'var(--rp-text)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedEntries.has(entry.id)}
                      onChange={() => onToggleEntry(entry.id)}
                      aria-label={`Select ${entry.name}`}
                      className="rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    />
                  </td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>{entry.name}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>{entry.email}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>
                    {entry.city && entry.state ? `${entry.city}, ${entry.state}` :
                     entry.city || entry.state || 'Not specified'}
                  </td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>{entry.motorcycle_type}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>{entry.source}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--rp-text)' }}>{new Date(entry.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => onResendEmail(entry)}
                        disabled={resendingEmail === entry.id}
                        aria-label={`Resend welcome email to ${entry.name}`}
                        className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {resendingEmail === entry.id ? (
                          <>
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : 'Resend Email'}
                      </button>
                      <button
                        onClick={() => onRequestDelete(entry.id)}
                        aria-label={`Delete ${entry.name}`}
                        className="admin-btn admin-btn-danger text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </>
);

export default WaitlistPanel;
